// Small shared pieces. Deliberately plain — the design language lives in
// @nulife/booking-ui's theme, and duplicating it here would be a second place
// for the product's look to drift.
//
// Every colour is a token with NO fallback, which is the platform rule for
// colour: a fallback makes a missing or half-applied theme look plausible.
// `--nl-danger` and `--nl-danger-soft` were the last two exceptions — declared
// nowhere and read only through their fallbacks, so no tenant theme could
// reach the one box in the product that says something failed. They are real
// tokens as of 2026-09-15.

export function Loading() {
  return (
    <p role="status" style={{ color: "var(--nl-ink-dim)", padding: 16 }}>
      Loading…
    </p>
  );
}

/**
 * Something the person needs to read.
 *
 * `tone="bad"` is for a failure. It is a separate visual state rather than the
 * same grey box, because "your hours were saved" and "your hours were not
 * saved" must never look alike — a glanced-at confirmation that was actually
 * an error is how somebody walks away believing a change was made.
 */
export function Message({ children, tone = "neutral", onDismiss }: {
  children: React.ReactNode;
  tone?: "neutral" | "bad";
  onDismiss?: () => void;
}) {
  return (
    <div
      role={tone === "bad" ? "alert" : "status"}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        background: tone === "bad" ? "var(--nl-danger-soft)" : "var(--nl-surface-raised)",
        color: tone === "bad" ? "var(--nl-danger)" : "var(--nl-ink)",
        border: `1px solid ${tone === "bad" ? "var(--nl-danger)" : "var(--nl-stroke)"}`,
        borderRadius: "var(--nl-radius-control)",
        padding: "10px 14px",
      }}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            border: "none", background: "none", color: "inherit",
            font: "inherit", cursor: "pointer", minHeight: "var(--nl-hit-min)",
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
